import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CircleCheck, CircleHelp, CircleX, Ellipsis, ShieldCheck, Trash2 } from 'lucide-react';
import { useCallback, useId, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { isApiError, type Versioned } from '../api/client';
import { useApi } from '../api/context';
import { queryKeys, useProviders, useSecretStores } from '../api/queries';
import type {
  CloudAccount,
  CloudAccountUsage,
  Diagnostic,
  VerificationCheck,
  VerificationCheckStatus,
  VerificationResult,
} from '../api/types';
import { usePermission } from '../auth/context';
import { deploymentStatusPresentation } from '../components/presentation';
import { useToast } from '../components/toast';
import { ActionButton, Button, Dialog, ErrorMessage, Loading, StatusBadge } from '../components/ui';
import { formatDay, formatDateTime, formatRelative } from '../lib/format';
import { MenuButton } from '../shell/Menu';
import { usePhone } from '../shell/breakpoints';
import { strings } from '../strings';
import { CloudAccountForm, ProviderTile, type CloudAccountValue } from './CloudAccountForm';
import { PROVIDERS } from './providers';

const s = strings.cloudAccounts;

function checkStatus(check: VerificationCheck): VerificationCheckStatus {
  return check.status ?? (check.passed ? 'passed' : 'failed');
}

const STATUS = {
  passed: { icon: CircleCheck, tone: 'text-success', word: s.passed },
  failed: { icon: CircleX, tone: 'text-danger', word: s.failed },
  unknown: { icon: CircleHelp, tone: 'text-fg-muted', word: s.unknown },
} as const;

/** The last verification (spec 6.6.3): who signed in, with which credential, and each check in three states. */
export function VerificationPanel({ result, at }: { result: VerificationResult | null; at: string | null }) {
  const titleId = useId();
  return (
    <section className="card ca-side" aria-labelledby={titleId} data-testid="last-verification">
      <div className="ca-side-head">
        <h3 id={titleId} className="ca-card-title">
          <ShieldCheck aria-hidden className="size-4" />
          {s.lastVerification}
        </h3>
        {result && (
          <span className="text-xs text-fg-muted" title={formatDateTime(at ?? result.checkedAt)}>
            {formatRelative(at ?? result.checkedAt)}
          </span>
        )}
      </div>
      {!result ? (
        <p className="help">{s.neverVerified}</p>
      ) : (
        <>
          {(result.identity || result.credentialSource) && (
            <p className="mb-2 text-sm">
              {result.identity && (
                <>
                  {s.signedInAs} <code className="font-mono">{result.identity}</code>
                  {result.credentialSource && ' · '}
                </>
              )}
              {result.credentialSource && (
                <span data-testid="credential-source">{result.credentialSource.description}</span>
              )}
            </p>
          )}
          <ul className="ca-checks">
            {result.checks.map((check) => {
              const status = checkStatus(check);
              const { icon: Icon, tone, word } = STATUS[status];
              return (
                <li key={check.name} className="ca-check" data-status={status} data-check={check.name}>
                  <Icon aria-hidden className={`mt-0.5 size-4 shrink-0 ${tone}`} />
                  <span className="min-w-0 flex-1">
                    <span className="sr-only">{word}: </span>
                    <span className={status === 'failed' ? 'text-danger' : undefined}>{check.message}</span>
                    {status === 'unknown' && <span className="badge tone-neutral ml-1">{s.unknown}</span>}
                    {status === 'failed' && s.hints[check.name] && (
                      <span className="help block">{s.hints[check.name]}</span>
                    )}
                  </span>
                  <code className="ca-check-name">{check.name}</code>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

function usageText(usage: CloudAccountUsage): string {
  if (usage.usages.length === 1 && usage.usages[0] === 'last-apply') return s.lastApplyOnly;
  return usage.usages.map((u) => s.usage[u] ?? u).join(' · ');
}

/** Used by (A9): the deployments that use the account, and how. */
export function UsedByPanel({ usedBy }: { usedBy: CloudAccountUsage[] }) {
  const titleId = useId();
  return (
    <section className="card ca-side" aria-labelledby={titleId} data-testid="used-by">
      <div className="ca-side-head">
        <h3 id={titleId} className="ca-card-title">
          {s.usedBy}
          <span className="ca-card-sub">{s.usedByCount(usedBy.length)}</span>
        </h3>
      </div>
      {usedBy.length === 0 ? (
        <p className="help">{s.usedByNone}</p>
      ) : (
        <ul className="ca-usage">
          {usedBy.map((usage) => (
            <li key={usage.deploymentId}>
              <Link className="link" to={`/deployments/${usage.deploymentId}`}>
                {usage.blueprintName} / {usage.deploymentName}
              </Link>
              <span className="text-xs text-fg-muted">{usageText(usage)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="help mt-2">{s.usedByExplain}</p>
    </section>
  );
}

function UsedByTable({ usedBy }: { usedBy: CloudAccountUsage[] }) {
  if (usedBy.length === 0) return <p className="help">{s.usedByNone}</p>;
  return (
    <>
      <div className="table-scroll">
        <table className="table table-cards">
          <thead>
            <tr>
              <th scope="col">{s.columns.deployment}</th>
              <th scope="col">{s.columns.blueprint}</th>
              <th scope="col">{s.columns.status}</th>
              <th scope="col">{s.columns.usage}</th>
            </tr>
          </thead>
          <tbody>
            {usedBy.map((usage) => (
              <tr key={usage.deploymentId}>
                <td data-label={s.columns.deployment}>
                  <Link className="link font-medium" to={`/deployments/${usage.deploymentId}`}>
                    {usage.deploymentName}
                  </Link>
                </td>
                <td data-label={s.columns.blueprint}>
                  <Link className="link" to={`/blueprints/${usage.blueprintId}`}>
                    {usage.blueprintName}
                  </Link>
                </td>
                <td data-label={s.columns.status}>
                  <StatusBadge presentation={deploymentStatusPresentation[usage.status]} />
                </td>
                <td data-label={s.columns.usage}>{usageText(usage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="help mt-2">{s.usedByExplain}</p>
    </>
  );
}

/** The detail pane of `/cloud-accounts/:id` (spec 6.6.2–6.6.6). */
export function CloudAccountDetail({ id }: { id: string }) {
  const api = useApi();
  const account = useQuery({ queryKey: queryKeys.cloudAccount(id), queryFn: () => api.getCloudAccount(id) });
  if (account.error) return <ErrorMessage error={account.error} />;
  if (!account.data) return <Loading />;
  return <Detail key={account.data.etag} account={account.data} />;
}

type Tab = 'settings' | 'used-by';

function Detail({ account }: { account: Versioned<CloudAccount> }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = usePermission('admin');
  const providers = useProviders();
  const stores = useSecretStores();
  const phone = usePhone();
  const formId = useId();
  const tabsId = useId();
  const [tab, setTab] = useState<Tab>('settings');
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [inUse, setInUse] = useState(false);
  const a = account.data;
  const providerName =
    providers.data?.find((p) => p.key === a.provider)?.displayName ??
    PROVIDERS[a.provider]?.displayName ??
    a.provider;
  const onDirtyChange = useCallback((value: boolean) => setDirty(value), []);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cloud-accounts'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.cloudAccount(a.id) });
  };

  const save = async (value: CloudAccountValue) => {
    setBusy(true);
    setDiagnostics([]);
    try {
      const saved = await api.updateCloudAccount(
        a.id,
        { name: value.name, scope: value.scope, auth: value.auth },
        account.etag,
      );
      queryClient.setQueryData(queryKeys.cloudAccount(a.id), saved);
      void queryClient.invalidateQueries({ queryKey: ['cloud-accounts'] });
      toast.show({ tone: 'success', title: s.saved });
    } catch (error) {
      if (isApiError(error) && error.diagnostics.length) setDiagnostics(error.diagnostics);
      else {
        toast.error(error);
        if (isApiError(error, 'PRECONDITION_FAILED')) refresh();
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      await api.verifyCloudAccount(a.id);
      refresh();
    } catch (error) {
      toast.error(error);
    } finally {
      setVerifying(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteCloudAccount(a.id, account.etag);
      void queryClient.invalidateQueries({ queryKey: ['cloud-accounts'] });
      navigate('/cloud-accounts');
    } catch (error) {
      if (isApiError(error, 'CLOUD_ACCOUNT_IN_USE')) {
        setInUse(true);
        refresh();
      } else {
        toast.error(error);
        setConfirmDelete(false);
      }
      setBusy(false);
    }
  };

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'settings', label: s.tabs.settings },
    { key: 'used-by', label: s.tabs.usedBy, count: a.usedBy.length },
  ];
  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const i = tabs.findIndex((t) => t.key === tab);
    let next = -1;
    if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (i + tabs.length - 1) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    setTab(tabs[next].key);
    document.getElementById(`${tabsId}-tab-${tabs[next].key}`)?.focus();
  };

  return (
    <div className="ca-detail-body">
      {phone && (
        <Link to="/cloud-accounts" className="link mb-3 inline-flex items-center gap-1 text-sm">
          <ArrowLeft aria-hidden className="size-4" />
          {s.back}
        </Link>
      )}
      <header className="ca-header">
        <ProviderTile provider={a.provider} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-xl font-semibold">{a.name}</h2>
          <p className="text-xs text-fg-muted">
            {s.meta(providerName, a.createdBy, formatDay(a.createdAt), a.revision)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ActionButton
            permission="admin"
            icon={<ShieldCheck aria-hidden className="size-4" />}
            disabledReason={verifying ? s.verifying : null}
            onClick={() => void verify()}
          >
            {verifying ? s.verifying : s.verify}
          </ActionButton>
          <MenuButton
            buttonClassName="btn btn-icon"
            buttonLabel={s.more(a.name)}
            buttonTitle={s.more(a.name)}
            buttonContent={<Ellipsis aria-hidden className="size-4" />}
            menuLabel={s.more(a.name)}
            placement="below-end"
            entries={[
              {
                kind: 'action',
                id: 'delete',
                label: s.deleteShort,
                icon: <Trash2 aria-hidden className="size-4" />,
                disabledReason: isAdmin ? undefined : strings.common.requiresRole('admin'),
                onSelect: () => {
                  setInUse(false);
                  setConfirmDelete(true);
                },
              },
            ]}
          />
        </div>
      </header>

      <div role="tablist" aria-label={s.tabsLabel} className="ca-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            id={`${tabsId}-tab-${t.key}`}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            aria-controls={`${tabsId}-panel`}
            tabIndex={tab === t.key ? 0 : -1}
            className="ca-tab"
            onClick={() => setTab(t.key)}
            onKeyDown={onTabKey}
          >
            {t.label}
            {t.count !== undefined && <span className="tw-count tw-count-neutral">{t.count}</span>}
          </button>
        ))}
      </div>
      <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-tab-${tab}`} className="pt-4">
        {tab === 'settings' ? (
          <div className="ca-settings">
            <div className="ca-settings-form">
              {!isAdmin && <p className="help mb-3">{s.viewOnly}</p>}
              <CloudAccountForm
                key={formKey}
                formId={formId}
                initial={a}
                providers={providers.data ?? []}
                secretStores={stores.data ?? []}
                readOnly={!isAdmin}
                diagnostics={diagnostics}
                onSubmit={(value) => void save(value)}
                onDirtyChange={onDirtyChange}
              />
              <div className="ca-savebar">
                <p className="help">{s.saveBar}</p>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button
                      disabled={!dirty || busy}
                      onClick={() => {
                        setDiagnostics([]);
                        setFormKey((k) => k + 1);
                      }}
                    >
                      {s.discard}
                    </Button>
                    <Button variant="primary" type="submit" form={formId} disabled={!dirty || busy}>
                      {busy ? strings.common.saving : s.saveChanges}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="ca-settings-side">
              <VerificationPanel result={a.lastVerification} at={a.lastVerifiedAt} />
              <UsedByPanel usedBy={a.usedBy} />
            </div>
          </div>
        ) : (
          <UsedByTable usedBy={a.usedBy} />
        )}
      </div>

      {confirmDelete && (
        <Dialog
          title={s.delete}
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <Button onClick={() => setConfirmDelete(false)}>{strings.common.cancel}</Button>
              {!inUse && (
                <Button variant="danger" disabled={busy} onClick={() => void remove()}>
                  {strings.common.delete}
                </Button>
              )}
            </>
          }
        >
          {inUse ? (
            <div role="alert">
              <p className="mb-2 text-sm">{s.inUse}</p>
              <ul className="ca-usage mb-2">
                {a.usedBy.map((usage) => (
                  <li key={usage.deploymentId}>
                    <Link className="link" to={`/deployments/${usage.deploymentId}`}>
                      {usage.blueprintName} / {usage.deploymentName}
                    </Link>
                    <span className="text-xs text-fg-muted">{usageText(usage)}</span>
                  </li>
                ))}
              </ul>
              <p className="help">{s.inUseList}</p>
            </div>
          ) : (
            <p data-autofocus tabIndex={-1} className="text-sm">
              {s.deleteConfirm(a.name)}
            </p>
          )}
        </Dialog>
      )}
    </div>
  );
}
